import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import { BusinessNewPayload } from '../types/payload'
import { createBusiness } from '../services'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { createBussinessSchema } from '../components/Create/schema'
import { TimeEntry } from '../components/Create/BusinessTimer'
import { allowsAllEmployeeCompanions } from '../types/utils'

// Policy enforcement date: 01/01/2026
const POLICY_START_DATE = dayjs('2026-01-01')

/**
 * Hook for Business Create page logic
 * Handles form, customer selection, time entries, and submission
 */
export const useBusinessCreate = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const queryClient = useQueryClient()
    const { t } = useTranslation('business')
    const { refetchCurrent } = useDataGridRefetch({ reactQueryKey: 'business' })

    // URL params
    const [searchParams] = useSearchParams()
    const journeyId = searchParams.get('customer_journey_id')
    const customerIdFromSearch = searchParams.get('customer_id')
    const businessTypeFromSearch = searchParams.get('business_type')
    const customerIdSearch = customerIdFromSearch
        ? Number(customerIdFromSearch)
        : undefined

    // Time entries state
    const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([
        {
            date: location.state?.startDate
                ? dayjs(location.state.startDate).format('YYYY-MM-DDTHH:mm:ss')
                : dayjs().format('YYYY-MM-DDTHH:mm:ss'),
            option: '2',
        },
    ])

    // Default business type
    let defaultBusinessType = businessTypeFromSearch ?? 'orthers'
    if (!businessTypeFromSearch && customerIdSearch !== undefined) {
        defaultBusinessType = 'meeting'
    }

    // Form setup
    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { isValid },
    } = useForm<BusinessNewPayload>({
        mode: 'onChange',
        defaultValues: {
            businesses: {
                business_type: defaultBusinessType,
                business_place: '',
                business_content: '',
                customer_id: customerIdSearch ?? 0,
                meeting_purpose: '',
                companions: [],
                metadata: {
                    days: [{}],
                    customer: {},
                },
            },
        },
        // @ts-ignore
        resolver: yupResolver(createBussinessSchema(t)),
    })

    const selectedBusinessType = watch('businesses.business_type')
    const selectedMeetingPurpose = watch('businesses.meeting_purpose')
    const allowsAllCompanions = allowsAllEmployeeCompanions(
        selectedMeetingPurpose
    )
    const previousAllowsAllCompanions = useRef(allowsAllCompanions)

    useEffect(() => {
        if (previousAllowsAllCompanions.current !== allowsAllCompanions) {
            setValue('businesses.companions', [])
        }
        previousAllowsAllCompanions.current = allowsAllCompanions
    }, [allowsAllCompanions, setValue])

    // Mutation
    const { mutateAsync: createBusinessMutation, isPending } =
        useMutationWithHandler(
            { mutationFn: createBusiness },
            {
                reactQueryKeys: QUERY_KEYS.BUSINESS,
                successMessage: t('create-success'),
                onSuccess: () => {
                    navigate('/account/businesses')
                    refetchCurrent()
                    queryClient.invalidateQueries({
                        queryKey: [QUERY_KEYS.BUSINESS_SUMMARY],
                    })
                },
            }
        )

    // Handlers
    const handleNoOptionCustomer = (inputValue: string) => {
        const encodedName = encodeURIComponent(inputValue)
        navigate(`/customers/new?customer_name=${encodedName}`, {
            state: { customer_name: inputValue },
        })
    }

    const handleAddDay = () => {
        const lastEntry = timeEntries[timeEntries.length - 1]
        let nextDay = dayjs(lastEntry.date).add(1, 'day')
        // Skip Sunday
        if (nextDay.day() === 0) {
            nextDay = nextDay.add(1, 'day')
        }
        setTimeEntries([
            ...timeEntries,
            { date: nextDay.format('YYYY-MM-DDTHH:mm:ss'), option: '2' },
        ])
    }

    // Policy validation
    const isPolicyActive =
        dayjs().isAfter(POLICY_START_DATE) ||
        dayjs().isSame(POLICY_START_DATE, 'day')

    const minDate = isPolicyActive
        ? dayjs().subtract(3, 'day').startOf('day')
        : undefined

    const dateValidation = useMemo(() => {
        if (!isPolicyActive) return { isValid: true, error: null }

        const today = dayjs().startOf('day')
        const earliestEntry = timeEntries.reduce((earliest, entry) => {
            const entryDate = dayjs(entry.date)
            return entryDate.isBefore(earliest) ? entryDate : earliest
        }, dayjs(timeEntries[0]?.date))

        const daysDiff = today.diff(earliestEntry.startOf('day'), 'day')

        if (daysDiff > 3) {
            return {
                isValid: false,
                error: t('policy_validation_error'),
            }
        }
        return { isValid: true, error: null }
    }, [timeEntries, isPolicyActive, t])

    // Submit handler
    const onSubmit: SubmitHandler<FieldValues> = async (data) => {
        const businessDays = timeEntries.map((entry) => {
            let startDate = dayjs(entry.date)
            let endDate = startDate.endOf('day')

            switch (entry.option) {
                case '0': // Morning
                    startDate = startDate.hour(8).minute(30).second(0)
                    endDate = endDate.hour(12).minute(0).second(0)
                    break
                case '1': // Afternoon
                    startDate = startDate.hour(13).minute(0).second(0)
                    endDate = endDate.hour(17).minute(30).second(0)
                    break
                case '2': // All day
                default:
                    startDate = startDate.hour(8).minute(30).second(0)
                    endDate = endDate.hour(17).minute(30).second(0)
                    break
            }

            return {
                business_startdate: startDate.format('YYYY-MM-DDTHH:mm:ss[Z]'),
                business_enddate: endDate.format('YYYY-MM-DDTHH:mm:ss[Z]'),
                business_option: entry.option,
            }
        })

        const firstEntry = timeEntries[0]
        const businessStartHour = firstEntry?.option === '1' ? 13 : 8
        const business_startdate = dayjs(firstEntry?.date ?? dayjs())
            .hour(businessStartHour)
            .minute(firstEntry?.option === '1' ? 0 : 30)
            .second(0)
            .format('YYYY-MM-DDTHH:mm:ss[Z]')

        const lastEntry = timeEntries[timeEntries.length - 1]
        const business_enddate = dayjs(lastEntry?.date ?? dayjs())
            .hour(lastEntry?.option === '0' ? 12 : 17)
            .minute(lastEntry?.option === '0' ? 0 : 30)
            .second(0)
            .format('YYYY-MM-DDTHH:mm:ss[Z]')

        const dataToSubmit: BusinessNewPayload = {
            ...data,
            businesses: {
                ...data.businesses,
                journey_id: journeyId ? Number(journeyId) : undefined,
                business_startdate,
                business_enddate,
                meeting_purpose:
                    data.businesses.business_type === 'meeting'
                        ? data.businesses.meeting_purpose
                        : undefined,
                companions: data.businesses.companions?.length
                    ? data.businesses.companions
                    : undefined,
                metadata: {
                    days: businessDays,
                    // The API hydrates the authoritative customer snapshot from
                    // businesses.customer_id during creation.
                    customer: null,
                },
            },
        }

        try {
            await createBusinessMutation(dataToSubmit)
        } catch (error) {
            console.error('Error submitting form', error)
        }
    }

    return {
        // Form
        control,
        handleSubmit,
        isValid,
        isPending,
        selectedBusinessType,
        selectedMeetingPurpose,

        // Customer
        customerIdSearch,
        handleNoOptionCustomer,

        // Time entries
        timeEntries,
        setTimeEntries,
        handleAddDay,
        minDate,

        // Validation
        dateValidation,
        journeyId,

        // Submit
        onSubmit,
        t,
    }
}
